// Editor for a clinic's first invoice number (the saleNumber the first invoice/receipt gets).
// Bespoke component because it needs a locked state and a hard confirmation modal, neither of
// which the shared form input supports. Seeds the per-clinic sale-number sequence through a
// dedicated endpoint; only editable while the clinic has issued no invoices.
import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import { validateFirstInvoiceNumber, canConfirm } from '../../lib/invoiceNumberStart';

@observer
class InvoiceNumberEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            locked: false,
            savedNumber: null,
            value: '',
            showModal: false,
            understood: false,
            saving: false,
            error: '',
        };
    }

    componentDidMount() {
        this.loadStatus();
    }

    loadStatus = async () => {
        const { customerId, isAdmin } = this.props;
        const query = isAdmin && customerId ? { customer: customerId } : {};
        const response = await util.fetchApi('/api/sales/invoice-start-number', { method: 'GET' }, query);
        const data = (response && response.data) || {};
        this.setState({
            loading: false,
            locked: !!data.locked,
            savedNumber: data.nextInvoiceNumber || null,
            value: data.nextInvoiceNumber > 0 ? String(data.nextInvoiceNumber) : '',
        });
    }

    openModal = () => {
        if (!validateFirstInvoiceNumber(this.state.value).ok) {
            return;
        }
        this.setState({ showModal: true, understood: false, error: '' });
    }

    closeModal = () => {
        this.setState({ showModal: false, understood: false });
    }

    confirm = async () => {
        const { customerId, isAdmin } = this.props;
        const { value, understood } = this.state;
        if (!canConfirm({ value, understood })) {
            return;
        }
        const validated = validateFirstInvoiceNumber(value);
        this.setState({ saving: true, error: '' });
        const body = { firstInvoiceNumber: validated.value };
        if (isAdmin && customerId) {
            body.customer = customerId;
        }
        const response = await util.fetchApi('/api/sales/invoice-start-number', { method: 'POST' }, body);
        if (response && response.status === 200) {
            this.setState({
                saving: false,
                showModal: false,
                understood: false,
                locked: false,
                savedNumber: validated.value,
            });
        } else {
            this.setState({
                saving: false,
                error: (response && response.message) || 'Could not save. Please try again.',
            });
        }
    }

    render() {
        const {
            loading, locked, savedNumber, value, showModal, understood, saving, error,
        } = this.state;
        if (loading) {
            return null;
        }
        const valid = validateFirstInvoiceNumber(value).ok;
        return (
            <div class='d-flex flex-column rounded-lg mx-3 mb-3 p-3 bg-body-secondary'>
                <div class='font-weight-lighter box-header mb-2'>
                    <i class='fa-duotone fa-hashtag me-2' />
                    <Text id='invoiceNumberStart.title'>First invoice number</Text>
                </div>

                {locked ? (
                    <div class='alert alert-secondary mb-0'>
                        <i class='fa-solid fa-lock me-2' />
                        {savedNumber > 0
                            ? (
                                <Text id='invoiceNumberStart.lockedWithNumber' fields={{ n: savedNumber }}>
                                    The invoice number series is in use and can no longer be changed. The first invoice number was {savedNumber}.
                                </Text>
                            ) : (
                                <Text id='invoiceNumberStart.locked'>
                                    The invoice number series is in use and can no longer be changed.
                                </Text>
                            )}
                    </div>
                ) : (
                    <div>
                        <p class='text-body-secondary small'>
                            <Text id='invoiceNumberStart.help'>
                                Set the number your first invoice (and receipt) will get. This sets your whole invoice and receipt number series, and can only be set before you create your first invoice.
                            </Text>
                        </p>
                        <div class='d-flex align-items-center gap-2' style='max-width: 320px;'>
                            <input
                                type='number'
                                class='form-control font-monospace'
                                value={value}
                                placeholder='2026297'
                                onInput={e => this.setState({ value: e.target.value })}
                            />
                            <button
                                type='button'
                                class='btn btn-primary text-nowrap'
                                onClick={this.openModal}
                                disabled={!valid}
                            >
                                <Text id='invoiceNumberStart.save'>Save</Text>
                            </button>
                        </div>
                    </div>
                )}

                {showModal && (
                    <div
                        class='modal d-block'
                        tabIndex='-1'
                        style='background-color: rgba(0,0,0,0.6); z-index: 1060;'
                        onClick={this.closeModal}
                    >
                        <div class='modal-dialog modal-dialog-centered' onClick={e => e.stopPropagation()}>
                            <div class='modal-content border border-4 border-danger shadow-lg' style='border-radius: 0;'>
                                <div class='modal-header bg-danger text-white' style='border-radius: 0;'>
                                    <h4 class='modal-title mb-0'>
                                        <i class='fa-solid fa-triangle-exclamation me-2' />
                                        <Text id='invoiceNumberStart.modalTitle'>Set invoice number series</Text>
                                    </h4>
                                </div>
                                <div class='modal-body py-4'>
                                    <p>
                                        <Text id='invoiceNumberStart.modalBody' fields={{ n: value }}>
                                            Your first invoice will get number {value}, and the series continues from there. This sets your permanent invoice and receipt number series and cannot be changed once you have created your first invoice.
                                        </Text>
                                    </p>
                                    <div class='form-check'>
                                        <input
                                            class='form-check-input'
                                            type='checkbox'
                                            id='invoiceNumberStartUnderstood'
                                            checked={understood}
                                            onChange={e => this.setState({ understood: e.target.checked })}
                                        />
                                        <label class='form-check-label' for='invoiceNumberStartUnderstood'>
                                            <Text id='invoiceNumberStart.understood'>
                                                I understand that this sets my invoice number series and cannot be changed after I create my first invoice.
                                            </Text>
                                        </label>
                                    </div>
                                    {error ? <div class='alert alert-danger mt-3 mb-0'>{error}</div> : null}
                                </div>
                                <div class='modal-footer'>
                                    <button type='button' class='btn btn-outline-secondary' onClick={this.closeModal} disabled={saving}>
                                        <Text id='invoiceNumberStart.cancel'>Cancel</Text>
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-danger'
                                        onClick={this.confirm}
                                        disabled={!canConfirm({ value, understood }) || saving}
                                    >
                                        {saving && <i class='fa-solid fa-circle-notch fa-spin me-1' />}
                                        <Text id='invoiceNumberStart.confirm'>Confirm</Text>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default InvoiceNumberEditor;
